import { useState, useMemo } from 'react';
import {
  Box, Typography, Stack, Chip, Button, Divider,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, TextField, InputAdornment, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { colors } from '../theme/designTheme';

const C = colors;

// ── Mock Data ─────────────────────────────────────────────────────────────────

const MOCK_AGENTS = [
  {
    id: 'agent01',
    name: 'Agent 01',
    role: 'agent',
    status: 'online',
    displayStatus: 'ONLINE',
    description: 'Abnormal number of accounts',
    redflagThreshold: '> 5 accounts',
    humanFeedbackCount: 214,
    specialization: ['Excessive accounts', 'Rapid account creation', 'Dormant→active spikes'],
    humanAgreements: 85,
    humanDisagreements: 15,
    llmAgreements: 92,
    llmDisagreements: 8,
    lastTrained: '2025-09-10',
    modelVersion: 'v1.6.3',
    throughputDay: 240,
    ahtMinutes: 2.8,
    coveragePct: 78,
    costPerCase: 0.013,
    driftScore: 22,
    uptimePct: 99.5,
    escalationRate: 4.2,
    falsePosRate: 6.1,
    falseNegRate: 1.8,
    latencyP95: 342,
    successRate: 99.2,
    availability: 99.5,
    sparkline: [86, 87, 88, 88, 89, 89, 90, 90, 89, 90, 91, 91, 92, 92, 92, 93, 93, 92, 92, 92],
    previousModels: [
      { version: 'v1.6.2', lastHA: 86, avg30dHA: 87, changedAt: '2025-08-28' },
      { version: 'v1.5.9', lastHA: 84, avg30dHA: 85, changedAt: '2025-08-02' },
    ],
  },
  {
    id: 'agent02',
    name: 'Agent 02',
    role: 'agent',
    status: 'offline',
    displayStatus: 'ANOMALOUS',
    description: 'U-turn transactions',
    redflagThreshold: 'Above 10,000 SGD within 30 days',
    humanFeedbackCount: 167,
    specialization: ['U-turn patterns', 'Mirror inflow/outflow', 'Round-trips <24h'],
    humanAgreements: 78,
    humanDisagreements: 22,
    llmAgreements: 88,
    llmDisagreements: 12,
    lastTrained: '2025-08-22',
    modelVersion: 'v2.1.0',
    throughputDay: 170,
    ahtMinutes: 3.4,
    coveragePct: 61,
    costPerCase: 0.018,
    driftScore: 36,
    uptimePct: 99.1,
    escalationRate: 6.7,
    falsePosRate: 9.4,
    falseNegRate: 2.5,
    latencyP95: 1204,
    successRate: 84.1,
    availability: 99.1,
    sparkline: [80, 80, 79, 79, 78, 78, 78, 79, 79, 80, 80, 81, 81, 81, 80, 80, 80, 79, 79, 78],
    previousModels: [
      { version: 'v2.0.7', lastHA: 83, avg30dHA: 84, changedAt: '2025-07-30' },
      { version: 'v2.0.3', lastHA: 86, avg30dHA: 85, changedAt: '2025-07-05' },
      { version: 'v1.9.9', lastHA: 82, avg30dHA: 83, changedAt: '2025-06-10' },
    ],
  },
  {
    id: 'agent03',
    name: 'Agent 03',
    role: 'agent',
    status: 'online',
    displayStatus: 'ONLINE',
    description: 'Transactions to sanctioned countries',
    redflagThreshold: 'Above 10,000 SGD within 30 days to Iran, Myanmar, Russia',
    humanFeedbackCount: 89,
    specialization: ['Sanctioned destinations', 'Jurisdiction routing', 'Watchlist hops'],
    humanAgreements: 90,
    humanDisagreements: 10,
    llmAgreements: 95,
    llmDisagreements: 5,
    lastTrained: '2025-07-14',
    modelVersion: 'v0.9.9',
    throughputDay: 120,
    ahtMinutes: 4.1,
    coveragePct: 54,
    costPerCase: 0.021,
    driftScore: 12,
    uptimePct: 96.3,
    escalationRate: 3.1,
    falsePosRate: 4.9,
    falseNegRate: 1.2,
    latencyP95: 188,
    successRate: 100,
    availability: 96.3,
    sparkline: [88, 89, 90, 90, 91, 91, 92, 92, 92, 93, 93, 93, 94, 94, 94, 95, 95, 95, 95, 95],
    previousModels: [{ version: 'v0.9.8', lastHA: 89, avg30dHA: 90, changedAt: '2025-06-18' }],
  },
  {
    id: 'master',
    name: 'Master Agent',
    role: 'master',
    status: 'online',
    displayStatus: 'ONLINE',
    specialization: ['Aggregation', 'Verification'],
    humanAgreements: 88,
    humanDisagreements: 12,
    llmAgreements: 90,
    llmDisagreements: 10,
    lastTrained: '2025-09-15',
    modelVersion: 'v3.0.0',
    throughputDay: 430,
    ahtMinutes: 1.7,
    coveragePct: 100,
    costPerCase: 0.007,
    driftScore: 18,
    uptimePct: 99.9,
    escalationRate: 2.1,
    falsePosRate: 5.2,
    falseNegRate: 1.6,
    latencyP95: 124,
    successRate: 100,
    availability: 99.8,
    sparkline: [86, 86, 86, 87, 87, 88, 88, 88, 89, 89, 89, 90, 90, 90, 90, 90, 90, 90, 90, 90],
    previousModels: [],
  },
];

const MOCK_FEEDBACK = {
  agent02: [
    {
      id: 'fbk-001',
      caseId: 'UTRN-2025-0911-4432',
      category: 'Legitimate Business Refund',
      text: 'Agent correctly identified corporate override policy for cross-border refunds exceeding standard thresholds.',
      submittedBy: { name: 'John Doe', initials: 'JG', color: '#3B82F6', role: 'L2 Audit' },
      submittedAt: '2025-08-24T10:32:00Z',
      status: 'approved',
    },
    {
      id: 'fbk-002',
      caseId: 'UTRN-2025-0909-1187',
      category: 'Payment Error / Recall',
      text: 'Possible hallucination in transaction hash validation. Verification required before escalation.',
      submittedBy: { name: 'Sarah Adams', initials: 'SA', color: '#8B5CF6', role: 'Analyst' },
      submittedAt: '2025-08-23T06:18:00Z',
      status: 'pending',
    },
    {
      id: 'fbk-003',
      caseId: 'UTRN-2025-0907-5529',
      category: 'Auth Bypass Pattern',
      text: 'Critical disagreement. Agent bypassed 2FA logic check on high-risk transaction corridor.',
      submittedBy: { name: 'Mike Kane', initials: 'MK', color: '#EC4899', role: 'Security' },
      submittedAt: '2025-08-22T04:02:00Z',
      status: 'flagged',
    },
  ],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const statusColor = (status) => {
  if (status === 'online') return C.online;
  if (status === 'offline') return C.critical;
  return C.warning;
};

const haColor = (ha) => (ha >= 85 ? C.low : ha >= 70 ? C.warning : C.critical);

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ── Mini sparkline bars (vertical) ───────────────────────────────────────────

const SparkBars = ({ values = [], color = C.accent }) => {
  const max = Math.max(...values, 1);
  const last8 = values.slice(-8);
  return (
    <Stack direction="row" spacing={0.4} alignItems="flex-end" sx={{ height: 24 }}>
      {last8.map((v, i) => (
        <Box
          key={i}
          sx={{
            width: 4,
            height: `${Math.max(4, (v / max) * 24)}px`,
            bgcolor: color,
            borderRadius: '2px',
            opacity: 0.6 + (i / last8.length) * 0.4,
          }}
        />
      ))}
    </Stack>
  );
};

// ── Donut Chart ───────────────────────────────────────────────────────────────

const DonutChart = ({ value, size = 148 }) => {
  const r = 50;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - value / 100);
  const color = haColor(value);
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="14" />
      <circle
        cx="60"
        cy="60"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="14"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
        transform="rotate(-90 60 60)"
      />
      <text
        x="60"
        y="55"
        textAnchor="middle"
        fill="white"
        fontSize="20"
        fontWeight="700"
        fontFamily="Lexend, sans-serif"
      >
        {value}%
      </text>
      <text
        x="60"
        y="72"
        textAnchor="middle"
        fill={value < 85 ? '#F59E0B' : C.low}
        fontSize="9"
        fontWeight="600"
        fontFamily="Lexend, sans-serif"
        letterSpacing="1"
      >
        {value < 85 ? 'FALLING' : 'STABLE'}
      </text>
    </svg>
  );
};

// ── Status Feedback Pill ──────────────────────────────────────────────────────

const FbkPill = ({ status }) => {
  const map = {
    approved: { label: 'APPROVED', bg: 'rgba(34,197,94,0.15)', color: C.low, border: C.lowBorder },
    pending:  { label: 'PENDING',  bg: C.highBg,               color: C.high, border: C.highBorder },
    flagged:  { label: 'FLAGGED',  bg: C.criticalBg,           color: C.critical, border: C.criticalBorder },
  };
  const s = map[status] || map.pending;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.4,
        borderRadius: '5px',
        bgcolor: s.bg,
        border: `1px solid ${s.border}`,
        fontFamily: 'Lexend',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: s.color,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </Box>
  );
};

// ── Metric Bar Row ────────────────────────────────────────────────────────────

const MetricBar = ({ label, value, max = 100, color }) => {
  const pct = Math.min((value / max) * 100, 100);
  const barColor = color || (pct >= 90 ? C.accent : pct >= 70 ? C.warning : C.critical);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', color: C.textSecondary }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: 'Lexend', fontSize: 12, fontWeight: 700, color: C.textPrimary }}>
          {value}{label.includes('Rate') ? '%' : label === 'LATENCY (P95)' ? 'MS' : '%'}
        </Typography>
      </Stack>
      <Box sx={{ height: 5, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: barColor, borderRadius: 99 }} />
      </Box>
    </Box>
  );
};

// ── Agent Card (Performance View) ─────────────────────────────────────────────

const AgentCard = ({ agent, onClick }) => {
  const isAnomalous = agent.status === 'offline';
  const accentCol = isAnomalous ? C.critical : C.accent;
  const latencyMax = 1500;

  return (
    <Box
      onClick={() => onClick(agent)}
      sx={{
        flex: 1,
        bgcolor: C.surface,
        border: `1px solid ${isAnomalous ? C.criticalBorder : C.border}`,
        borderRadius: 2,
        p: 2.5,
        cursor: 'pointer',
        transition: 'all 0.18s',
        '&:hover': {
          bgcolor: C.surfaceHover,
          borderColor: accentCol,
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 32px rgba(0,0,0,0.4)`,
        },
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: isAnomalous ? C.criticalBg : C.accentDim,
              border: `1px solid ${isAnomalous ? C.criticalBorder : C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
            }}
          >
            🤖
          </Box>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 14, fontWeight: 700, color: C.textPrimary }}>
            {agent.name.toUpperCase()}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            py: 0.3,
            borderRadius: '5px',
            bgcolor: isAnomalous ? C.criticalBg : 'rgba(34,197,94,0.12)',
            border: `1px solid ${isAnomalous ? C.criticalBorder : 'rgba(34,197,94,0.30)'}`,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: isAnomalous ? C.critical : C.online,
              boxShadow: `0 0 5px ${isAnomalous ? C.critical : C.online}`,
            }}
          />
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 700, color: isAnomalous ? C.critical : C.online, letterSpacing: '0.06em' }}>
            {agent.displayStatus}
          </Typography>
        </Box>
      </Stack>

      {/* Metrics */}
      <Stack spacing={1.75}>
        <MetricBar label="LATENCY (P95)" value={agent.latencyP95} max={latencyMax} color={isAnomalous ? C.critical : C.accent} />
        <MetricBar label="SUCCESS RATE" value={agent.successRate} color={isAnomalous ? C.critical : C.accent} />
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.75}>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', color: C.textSecondary }}>
              AGREEMENT
            </Typography>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 12, fontWeight: 700, color: C.textPrimary }}>
              {agent.humanAgreements}%
            </Typography>
          </Stack>
          <SparkBars values={agent.sparkline} color={isAnomalous ? C.critical : C.accent} />
        </Box>
      </Stack>
    </Box>
  );
};

// ── Master Card (Performance View) ────────────────────────────────────────────

const MasterCard = ({ agent, onClick }) => (
  <Box
    onClick={() => onClick(agent)}
    sx={{
      bgcolor: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 2,
      p: 3,
      maxWidth: 480,
      mx: 'auto',
      position: 'relative',
      cursor: 'pointer',
      transition: 'all 0.18s',
      '&:hover': {
        bgcolor: C.surfaceHover,
        borderColor: C.accent,
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 32px rgba(0,0,0,0.4)`,
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -11,
        left: '50%',
        transform: 'translateX(-50%)',
        px: 1.5,
        py: 0.3,
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: '5px',
        fontFamily: 'Lexend',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.12em',
        color: C.textSecondary,
      }}
    >
      MASTERHUB
    </Box>

    <Stack direction="row" alignItems="center" spacing={2}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: C.accentDim,
          border: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        🔷
      </Box>
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 18, fontWeight: 700, color: C.textPrimary, letterSpacing: '0.04em' }}>
              MASTER AGENT
            </Typography>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 500, color: C.textSecondary, letterSpacing: '0.08em' }}>
              SYSTEM ORCHESTRATOR
            </Typography>
          </Box>
          <Stack alignItems="flex-end">
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 22, fontWeight: 700, color: C.accent }}>
              {agent.availability}%
            </Typography>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 9, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.08em' }}>
              AVAILABILITY
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Stack>

    <Divider sx={{ my: 2, borderColor: C.border }} />

    <Stack direction="row" spacing={3}>
      {[
        { label: 'P95', value: `${agent.latencyP95}ms` },
        { label: 'SR', value: `${agent.successRate}%` },
        { label: 'HUMAN', value: `${agent.humanAgreements}%` },
      ].map(({ label, value }) => (
        <Box key={label}>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 9, fontWeight: 700, color: C.textSecondary, letterSpacing: '0.1em', mb: 0.25 }}>
            {label}
          </Typography>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 15, fontWeight: 700, color: C.textPrimary }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);

// ── Agent Performance View ─────────────────────────────────────────────────────

const AgentPerformanceView = ({ onSelectAgent }) => {
  const agents = MOCK_AGENTS.filter((a) => a.role === 'agent');
  const master = MOCK_AGENTS.find((a) => a.role === 'master');

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Box>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', color: C.textSecondary, mb: 0.75 }}>
            ADMINISTRATION / AGENT_ORCHESTRATION
          </Typography>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 30, fontWeight: 700, letterSpacing: '0.06em', color: C.textPrimary }}>
            AGENT PERFORMANCE
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.08em' }}>
              SYSTEM THROUGHPUT
            </Typography>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 22, fontWeight: 700, color: C.accent }}>
              14.2k <span style={{ fontSize: 12, color: C.textSecondary }}>req/s</span>
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderColor: C.border }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.08em' }}>
              ACTIVE SESSIONS
            </Typography>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 22, fontWeight: 700, color: C.high }}>
              1,208
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Orchestration status */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3.5}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            bgcolor: C.accentDim,
            border: `1px solid ${C.border}`,
            borderRadius: '6px',
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: C.accent, boxShadow: `0 0 6px ${C.accent}` }} />
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.1em' }}>
            ACTIVE ORCHESTRATION
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: 'Lexend', fontSize: 12, color: C.textSecondary }}>
          NETWORK LOAD: <span style={{ color: C.textPrimary, fontWeight: 600 }}>64.2%</span>
        </Typography>
      </Stack>

      {/* Master Agent */}
      <Box mb={3}>
        <MasterCard agent={master} onClick={onSelectAgent} />
      </Box>

      {/* Agent Cards */}
      <Stack direction="row" spacing={2}>
        {agents.map((agent) => (
          <AgentCard key={agent.id} agent={agent} onClick={onSelectAgent} />
        ))}
      </Stack>
    </Box>
  );
};

// ── Agent Detail View ─────────────────────────────────────────────────────────

const AgentDetailView = ({ agent, onBack }) => {
  const [feedbackStore, setFeedbackStore] = useState(MOCK_FEEDBACK);
  const [textQuery, setTextQuery] = useState('');
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertChoice, setRevertChoice] = useState('');
  const [toast, setToast] = useState('');

  const feedback = useMemo(() => {
    const all = feedbackStore[agent.id] || [];
    if (!textQuery.trim()) return all;
    const q = textQuery.toLowerCase();
    return all.filter((f) => f.text.toLowerCase().includes(q) || f.caseId.toLowerCase().includes(q));
  }, [feedbackStore, agent.id, textQuery]);

  const isAnomalous = agent.status === 'offline';

  const removeFeedback = (fbkId) => {
    setFeedbackStore((prev) => ({
      ...prev,
      [agent.id]: (prev[agent.id] || []).filter((f) => f.id !== fbkId),
    }));
    setToast('Feedback removed');
  };

  const thSx = {
    fontFamily: 'Lexend',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: C.textSecondary,
    bgcolor: C.surfaceElevated,
    borderBottom: `1px solid ${C.border}`,
    py: 1.5,
    px: 2,
  };
  const tdSx = {
    borderBottom: `1px solid ${C.borderSubtle}`,
    py: 1.75,
    px: 2,
    verticalAlign: 'top',
  };

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh', p: 3 }}>
      {/* Breadcrumb + back */}
      <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
        <IconButton
          size="small"
          onClick={onBack}
          sx={{ color: C.textSecondary, '&:hover': { color: C.accent } }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', color: C.textSecondary }}>
          ADMINISTRATION / AGENT_ORCHESTRATION / {agent.id.toUpperCase()}
        </Typography>
      </Stack>

      {/* Title row */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2.5}>
        <Box>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 28, fontWeight: 700, letterSpacing: '0.06em', color: C.textPrimary }}>
            {agent.name.toUpperCase()} ANALYSIS &amp; FEEDBACK
          </Typography>

          {/* Status badges */}
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.25,
                py: 0.4,
                borderRadius: '5px',
                bgcolor: isAnomalous ? C.criticalBg : 'rgba(34,197,94,0.12)',
                border: `1px solid ${isAnomalous ? C.criticalBorder : 'rgba(34,197,94,0.30)'}`,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isAnomalous ? C.critical : C.online }} />
              <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 700, color: isAnomalous ? C.critical : C.online, letterSpacing: '0.06em' }}>
                {isAnomalous ? 'OFFLINE' : 'ONLINE'}
              </Typography>
            </Box>
            <Chip
              label={`Training: ${agent.modelVersion}`}
              size="small"
              sx={{ fontFamily: 'Lexend', fontSize: 10, bgcolor: C.surfaceElevated, color: C.textSecondary, border: `1px solid ${C.border}` }}
            />
            <Chip
              label={`Last Trained ${fmtDate(agent.lastTrained)}`}
              size="small"
              sx={{ fontFamily: 'Lexend', fontSize: 10, bgcolor: C.surfaceElevated, color: C.textSecondary, border: `1px solid ${C.border}` }}
            />
          </Stack>
        </Box>

        {/* Action buttons */}
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
          <Button
            startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 15 }} />}
            variant="contained"
            size="small"
            sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 600, bgcolor: C.accent, color: '#000', letterSpacing: '0.06em', '&:hover': { bgcolor: '#00B8D9' } }}
          >
            RETRAIN
          </Button>
          <Button
            startIcon={<PauseCircleOutlineIcon sx={{ fontSize: 15 }} />}
            variant="outlined"
            size="small"
            sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 600, color: C.textSecondary, borderColor: C.border, letterSpacing: '0.06em', '&:hover': { borderColor: C.accent, color: C.accent } }}
          >
            PAUSE
          </Button>
          <Button
            startIcon={<ShareOutlinedIcon sx={{ fontSize: 15 }} />}
            variant="outlined"
            size="small"
            sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 600, color: C.textSecondary, borderColor: C.border, letterSpacing: '0.06em', '&:hover': { borderColor: C.accent, color: C.accent } }}
          >
            ROUTE RULES
          </Button>
          {isAnomalous && (
            <Button
              startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
              variant="outlined"
              size="small"
              onClick={() => { setRevertChoice(''); setRevertOpen(true); }}
              sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 600, color: C.critical, borderColor: C.criticalBorder, letterSpacing: '0.06em', '&:hover': { bgcolor: C.criticalBg } }}
            >
              REVERT
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Main metrics grid */}
      <Stack direction="row" spacing={2} mb={3} alignItems="stretch">
        {/* Left: stat tiles */}
        <Stack spacing={1.5} sx={{ minWidth: 160 }}>
          {[
            { label: 'THROUGHPUT', value: agent.throughputDay.toLocaleString(), sub: 'cases/day average' },
            { label: 'AVG HANDLE TIME', value: `${agent.ahtMinutes} min`, sub: 'Standard target met' },
            { label: 'COST/CASE', value: `$${agent.costPerCase.toFixed(3)}`, sub: 'OPTIMIZED', subColor: C.low },
            { label: 'UPTIME', value: `${agent.uptimePct}%`, sub: 'Last 30 days session' },
          ].map(({ label, value, sub, subColor }) => (
            <Box
              key={label}
              sx={{
                bgcolor: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                p: 2,
                flex: 1,
              }}
            >
              <Typography sx={{ fontFamily: 'Lexend', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: C.textSecondary, mb: 0.5 }}>
                {label}
              </Typography>
              <Typography sx={{ fontFamily: 'Lexend', fontSize: 22, fontWeight: 700, color: C.textPrimary, lineHeight: 1.1 }}>
                {value}
              </Typography>
              <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, color: subColor || C.textSecondary, mt: 0.5 }}>
                {sub}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Center: Human Agreement */}
        <Box
          sx={{
            flex: 1,
            bgcolor: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: C.textSecondary, mb: 2 }}>
            HUMAN AGREEMENT
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <DonutChart value={agent.humanAgreements} size={160} />
          </Box>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 12, color: C.textSecondary, textAlign: 'center', lineHeight: 1.6 }}>
            System-Human consensus is drifting{' '}
            {agent.humanAgreements < 85 ? 'below critical threshold.' : 'within acceptable range.'}
          </Typography>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, color: C.textMuted, textAlign: 'center', mt: 1.5, fontStyle: 'italic' }}>
            * Based on {agent.humanFeedbackCount?.toLocaleString() || '4,200'} audit samples
          </Typography>
        </Box>

        {/* Right: Cross-model + Drift */}
        <Stack spacing={1.5} sx={{ minWidth: 200 }}>
          <Box
            sx={{
              bgcolor: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              p: 2.5,
              flex: 1,
            }}
          >
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: C.textSecondary, mb: 2 }}>
              CROSS-MODEL METRICS
            </Typography>
            <Stack spacing={1.5}>
              <Box>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, color: C.textSecondary, letterSpacing: '0.06em' }}>LLM AGREEMENT</Typography>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{agent.llmAgreements}%</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={agent.llmAgreements}
                  sx={{
                    height: 5,
                    borderRadius: 99,
                    bgcolor: 'rgba(255,255,255,0.07)',
                    '& .MuiLinearProgress-bar': { bgcolor: C.accent, borderRadius: 99 },
                  }}
                />
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, color: C.textSecondary, letterSpacing: '0.06em' }}>HUMAN DISAGREEMENT</Typography>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 14, fontWeight: 700, color: C.critical }}>{agent.humanDisagreements}%</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={agent.humanDisagreements}
                  sx={{
                    height: 5,
                    borderRadius: 99,
                    bgcolor: 'rgba(255,255,255,0.07)',
                    '& .MuiLinearProgress-bar': { bgcolor: C.critical, borderRadius: 99 },
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Drift Score */}
          <Box
            sx={{
              bgcolor: C.surface,
              border: `1px solid ${agent.driftScore > 30 ? C.highBorder : C.border}`,
              borderRadius: 2,
              p: 2.5,
              flex: 1,
            }}
          >
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: C.textSecondary, mb: 1 }}>
              INTELLIGENCE DRIFT SCORE
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Lexend',
                fontSize: 44,
                fontWeight: 700,
                color: agent.driftScore > 30 ? C.high : C.low,
                lineHeight: 1,
                mb: 1,
              }}
            >
              {agent.driftScore}
            </Typography>
            {agent.driftScore > 30 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.3,
                  bgcolor: C.highBg,
                  border: `1px solid ${C.highBorder}`,
                  borderRadius: '5px',
                  mb: 1,
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 11, color: C.high }} />
                <Typography sx={{ fontFamily: 'Lexend', fontSize: 9, fontWeight: 700, color: C.high, letterSpacing: '0.08em' }}>
                  AMBER ALERT
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, color: C.textSecondary, lineHeight: 1.5 }}>
              Model output distribution has shifted {agent.driftScore > 20 ? '12.4%' : '5.1%'} from baseline training data.
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Feedback Review Loop */}
      <Box
        sx={{
          bgcolor: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Section header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}
        >
          <Box>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 14, fontWeight: 700, color: C.textPrimary, letterSpacing: '0.04em' }}>
              FEEDBACK REVIEW LOOP
            </Typography>
            <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, color: C.textSecondary, letterSpacing: '0.08em' }}>
              AUDIT LOGS &amp; RESOLUTION QUEUE
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder="Search feedback..."
              value={textQuery}
              onChange={(e) => setTextQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: C.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 220,
                '& .MuiOutlinedInput-root': {
                  bgcolor: C.surfaceElevated,
                  color: C.textPrimary,
                  fontFamily: 'Lexend',
                  fontSize: 12,
                  '& fieldset': { borderColor: C.border },
                  '&:hover fieldset': { borderColor: C.accent },
                  '&.Mui-focused fieldset': { borderColor: C.accent },
                },
                '& input::placeholder': { color: C.textMuted, opacity: 1 },
              }}
            />
            <IconButton
              size="small"
              sx={{ color: C.textSecondary, border: `1px solid ${C.border}`, borderRadius: '6px', p: 0.75, '&:hover': { borderColor: C.accent, color: C.accent } }}
            >
              <FilterListIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Table */}
        <Table>
          <TableHead>
            <TableRow>
              {['STATUS', 'CATEGORY', 'DESCRIPTION', 'SUBMITTER', 'DATE', 'ACTIONS'].map((col) => (
                <TableCell key={col} sx={thSx}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {feedback.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: C.textSecondary, fontFamily: 'Lexend', fontSize: 12 }}>
                  No feedback found
                </TableCell>
              </TableRow>
            )}
            {feedback.map((fbk) => (
              <TableRow key={fbk.id} sx={{ '&:hover': { bgcolor: C.surfaceHover } }}>
                <TableCell sx={tdSx}>
                  <FbkPill status={fbk.status} />
                </TableCell>
                <TableCell sx={tdSx}>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 600, color: C.textSecondary, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {fbk.category}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...tdSx, maxWidth: 320 }}>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 12, color: C.textSecondary, lineHeight: 1.55 }}>
                    {fbk.text.length > 100 ? fbk.text.slice(0, 97) + '…' : fbk.text}
                  </Typography>
                </TableCell>
                <TableCell sx={tdSx}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: fbk.submittedBy.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'Lexend',
                        fontSize: 9,
                        fontWeight: 700,
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      {fbk.submittedBy.initials}
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 600, color: C.textPrimary, whiteSpace: 'nowrap' }}>
                        {fbk.submittedBy.name}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Lexend', fontSize: 9, color: C.textSecondary }}>
                        ({fbk.submittedBy.role})
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={tdSx}>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 11, color: C.textSecondary, whiteSpace: 'nowrap' }}>
                    {fmtDate(fbk.submittedAt)}
                  </Typography>
                </TableCell>
                <TableCell sx={tdSx}>
                  <IconButton
                    size="small"
                    onClick={() => removeFeedback(fbk.id)}
                    sx={{ color: C.textMuted, borderRadius: '6px', p: 0.5, '&:hover': { color: C.critical, bgcolor: C.criticalBg } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Table footer */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 1.5, borderTop: `1px solid ${C.border}` }}
        >
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 10, color: C.textSecondary, letterSpacing: '0.06em' }}>
            DISPLAYING 1-{feedback.length} OF {(feedbackStore[agent.id] || []).length} RECORDS
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton size="small" sx={{ color: C.textSecondary, fontSize: 14 }}>{'<'}</IconButton>
            {[1, 2].map((n) => (
              <Box
                key={n}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  bgcolor: n === 1 ? C.accent : 'transparent',
                  border: `1px solid ${n === 1 ? C.accent : C.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontFamily: 'Lexend', fontSize: 11, fontWeight: 600, color: n === 1 ? '#000' : C.textSecondary }}>
                  {n}
                </Typography>
              </Box>
            ))}
            <IconButton size="small" sx={{ color: C.textSecondary, fontSize: 14 }}>{'>'}</IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Revert Dialog */}
      <Dialog
        open={revertOpen}
        onClose={() => setRevertOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: C.surfaceElevated,
            border: `1px solid ${C.border}`,
            color: C.textPrimary,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Lexend', fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
          Revert to Previous Model
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: 13, color: C.textSecondary, mb: 2 }}>
            Select a previous model version to restore:
          </Typography>
          <RadioGroup value={revertChoice} onChange={(e) => setRevertChoice(e.target.value)}>
            {(agent.previousModels || []).map((m) => (
              <FormControlLabel
                key={m.version}
                value={m.version}
                control={<Radio sx={{ color: C.textSecondary, '&.Mui-checked': { color: C.accent } }} />}
                label={
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: 13, color: C.textPrimary }}>
                    {m.version} — HA {m.lastHA}% (avg {m.avg30dHA}%) · {fmtDate(m.changedAt)}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevertOpen(false)} sx={{ fontFamily: 'Lexend', color: C.textSecondary }}>
            Cancel
          </Button>
          <Button
            disabled={!revertChoice}
            variant="contained"
            onClick={() => { setRevertOpen(false); setToast(`Reverted to ${revertChoice}`); }}
            sx={{ fontFamily: 'Lexend', bgcolor: C.accent, color: '#000', '&:hover': { bgcolor: '#00B8D9' } }}
          >
            Confirm Revert
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────

export default function AgentGraphPanel() {
  const [selected, setSelected] = useState(null);

  if (selected) {
    return <AgentDetailView agent={selected} onBack={() => setSelected(null)} />;
  }
  return <AgentPerformanceView onSelectAgent={setSelected} />;
}
